import { useState } from 'react'
import React from 'react'

import {
  CreateProcurementDefaultTypeFormDocument,
  FormsElementTypeEnum,
  ProcurementTypeItemFragment,
  RequestTypeDetailDocument,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import app from 'modules/core/store/models/app'

import { Form, FormEditor, FormPreviewInline } from 'modules/forms'
import RequestTypeDetailPageEmptyState from 'modules/procurement/pages/RequestTypeDetailPage/emptyState'

const PERMITTED_FIELD_TYPES: FormsElementTypeEnum[] = [
  'longtext',
  'multiselect',
  'number',
  'section',
  'select',
  'text',
  'url',
  'dropdownsingle',
  'date',
]

interface Props {
  requestType: ProcurementTypeItemFragment
  isCustomFormsEnabled: boolean
  form?: Form
  onPreviewClick: () => void
}

const FormTab = ({ requestType, isCustomFormsEnabled, form, onPreviewClick }: Props) => {
  const { access } = useCurrentSession()

  const formIsDraft = form?.status === 'draft'
  const isFormEmpty = form?.elements.length === 0

  const shouldShowEmptyState = !form || isFormEmpty
  const [showEmptyState, setShowEmptyState] = useState(shouldShowEmptyState)

  const [createProcurementDefaultTypeFormMutation, { loading: isCreatingForm }] = useMutation(
    CreateProcurementDefaultTypeFormDocument,
    {
      extractorFn: (data) => ({
        ...data.procurementMutations.createFormProcurementType,
      }),
      refetchQueries: [
        {
          query: RequestTypeDetailDocument,
          variables: {
            requestTypeId: requestType.id,
            companyId: requestType.companyId,
          },
        },
      ],
    }
  )

  const handleCreateForm = React.useCallback(
    async (procurementType: ProcurementTypeItemFragment) => {
      const result = await createProcurementDefaultTypeFormMutation({
        variables: {
          companyId: procurementType.companyId,
          description: procurementType.description,
          title: procurementType.name,
          resourceType: 'Procurement::Type',
          typeId: procurementType.id,
          authorId: access.id,
        },
      })

      const handled = handleResult(result, () => {
        app.setFlash({
          type: 'wrong',
          message: 'errors_procurement_create_form_error',
        })
      })
      if (handled.success) {
        setShowEmptyState(false)
      }
    },
    [createProcurementDefaultTypeFormMutation, access.id]
  )

  if (!isCustomFormsEnabled) {
    return null
  }

  const renderForm = () => {
    if (!form) return null

    return formIsDraft ? (
      <FormEditor
        formId={form.id}
        resourceType="Procurement::Type"
        permittedFieldTypes={PERMITTED_FIELD_TYPES}
        showElementName={false}
        titleSize="small"
      />
    ) : (
      <FormPreviewInline
        formId={form.id}
        resourceId={requestType?.id?.toString() || ''}
        resourceType="Procurement::Type"
        showElementName={false}
        titleSize="small"
      />
    )
  }

  return (
    <>
      {showEmptyState ? (
        <RequestTypeDetailPageEmptyState
          onActionClick={
            !form ? () => handleCreateForm(requestType) : () => setShowEmptyState(false)
          }
          onPreviewClick={onPreviewClick}
          isLoading={isCreatingForm}
        />
      ) : (
        renderForm()
      )}
    </>
  )
}

export default FormTab
